<template>
  <div>
    <h6
      class="heading-small text-muted mb-4 text-truncate"
      style="padding: 1rem; border-bottom: 1px solid #e9ecef"
    >
      CLP User Site Page Setup
    </h6>

    <form id="form-proper">
      <div class="row">
        <div class="col-2">
          <a-steps :current="currentStep" direction="vertical">
            <a-step description="Site Definition" />
            <a-step description="Site Settings" />
            <a-step description="Field Settings" />
            <a-step description="Published Site" />
          </a-steps>
        </div>

        <div class="col-10">
          <div v-if="currentStep == 0">
            <h3>CLP User Site Page Details</h3>
            <br />
            <div class="col-8">
              <base-input
                label="Site Title"
                input-classes="form-control-sm"
                :disabled="viewMode"
                required
              >
                <a-input v-model="formData.site_title" :disabled="viewMode" />
                <div v-if="$v.formData.site_title.$dirty" class="text-danger">
                  <div class="error" v-if="!$v.formData.site_title.required">
                    Field is required
                  </div>
                </div>
                <small class="text-muted">Example: Sample Store</small>
              </base-input>

              <br />
              <base-input label="Membership Program" required>
                <br />
                <a-select
                  :disabled="viewMode"
                  v-model="formData.membership_program_id"
                  @change="getFieldSettings"
                >
                  <a-select-option
                    v-for="membershipProgram in options.membershipPrograms"
                    :key="membershipProgram.id"
                    :value="membershipProgram.id"
                  >
                    {{ membershipProgram.name }}
                  </a-select-option>
                </a-select>
                <div
                  v-if="$v.formData.membership_program_id.$dirty"
                  class="text-danger"
                >
                  <div
                    class="error"
                    v-if="!$v.formData.membership_program_id.required"
                  >
                    Field is required
                  </div>
                </div>
              </base-input>

              <base-input label="Site Status">
                <br />

                <a-select
                  :disabled="viewMode"
                  v-model="formData.site_status"
                  required
                >
                  <a-select-option
                    v-for="status in options.siteStatuses"
                    :key="status.key"
                    :value="status.key"
                  >
                    {{ status.value }}
                  </a-select-option>
                </a-select>
                <div v-if="$v.formData.site_status.$dirty" class="text-danger">
                  <div class="error" v-if="!$v.formData.site_status.required">
                    Field is required
                  </div>
                </div>
              </base-input>
            </div>

            <hr />

            <h3>Sub Domain Setup</h3>
            <br />

            <div class="col-6">
              <base-input
                label="Sub Domain Name"
                input-classes="form-control-sm"
              >
                <a-input
                  :addon-before="domainName"
                  v-model="formData.sub_domain"
                  :disabled="viewMode"
                />
              </base-input>
            </div>
          </div>

          <div v-if="currentStep == 1">
            <h3>Features</h3>
            <br />

            <div class="col">
              <!-- 1st row -->
              <div class="row mb-4">
                <div class="col-3">
                  <div class="row">
                    <div class="col-1">
                      <a-checkbox
                        v-model="formData.allow_points_viewing"
                        :disabled="viewMode"
                      >
                      </a-checkbox>
                    </div>
                    <div class="col">
                      <span><strong>Points Viewing</strong></span>
                      <br />
                      <span class="text-muted"
                        >Allow customers to see their points online.</span
                      >
                    </div>
                  </div>
                </div>

                <div class="col-3">
                  <div class="row">
                    <div class="col-1">
                      <a-checkbox
                        v-model="formData.display_banner_image"
                        :disabled="viewMode"
                      >
                      </a-checkbox>
                    </div>
                    <div class="col">
                      <span><strong>Display Image Banner</strong></span>
                      <br />
                      <span class="text-muted"
                        >Allow Banner Ads to be shown in the site.</span
                      >
                    </div>
                  </div>
                </div>
              </div>
              <!-- /1st row -->

              <!-- 2nd row -->
              <div class="row mb-4">
                <div class="col-3">
                  <div class="row">
                    <div class="col-1">
                      <a-checkbox
                        v-model="formData.allow_self_registration"
                        :disabled="viewMode"
                      >
                      </a-checkbox>
                    </div>
                    <div class="col">
                      <span><strong>Self Registration</strong></span>
                      <br />
                      <span class="text-muted"
                        >Allow customers to register themselves to the
                        membership program.</span
                      >
                    </div>
                  </div>
                </div>

                <div class="col-3">
                  <div class="row">
                    <div class="col-1">
                      <a-checkbox
                        v-model="formData.display_site_text"
                        :disabled="viewMode"
                      >
                      </a-checkbox>
                    </div>
                    <div class="col">
                      <span><strong>Display Site Text</strong></span>
                      <br />
                      <span class="text-muted"
                        >Allow text announcements to be shown in the site.</span
                      >
                    </div>
                  </div>
                </div>
              </div>
              <!-- /2nd row -->

              <!-- 3rd row -->
              <div class="row mb-4">
                <div class="col-3">
                  <div class="row">
                    <div class="col-1">
                      <a-checkbox
                        v-model="formData.allow_self_update"
                        :disabled="viewMode"
                      >
                      </a-checkbox>
                    </div>
                    <div class="col">
                      <span><strong>Profile Self Update</strong></span>
                      <br />
                      <span class="text-muted"
                        >Allow customers to update their personal information.
                      </span>
                    </div>
                  </div>
                </div>

                <div class="col-3">
                  <div class="row">
                    <div class="col-1">
                      <a-checkbox
                        v-model="formData.allow_login_with_pin"
                        :disabled="viewMode"
                      >
                      </a-checkbox>
                    </div>
                    <div class="col">
                      <span><strong>Login with PIN vs OTP</strong></span>
                      <br />
                      <span class="text-muted"
                        >Allow customers to login their nominated PIN after
                        initial registration.</span
                      >
                    </div>
                  </div>
                </div>
              </div>
              <!-- /3rd row -->

              <!-- 4th row -->
              <div class="row mb-4">
                <div class="col-3">
                  <div class="row">
                    <div class="col-1">
                      <a-checkbox
                        v-model="formData.allow_registration_without_purchase"
                        :disabled="viewMode"
                      >
                      </a-checkbox>
                    </div>
                    <div class="col">
                      <span
                        ><strong
                          >Allow Registration without Purchase</strong
                        ></span
                      >
                      <br />
                      <span class="text-muted"
                        >Allow customers to self register even if they have no
                        previously recorded transaction.
                      </span>
                    </div>
                  </div>
                </div>

                <div class="col-3">
                  <div class="row">
                    <div class="col-1">
                      <a-checkbox
                        v-model="formData.allow_trx_history_view"
                        :disabled="viewMode"
                      >
                      </a-checkbox>
                    </div>
                    <div class="col">
                      <span><strong>Show Transaction History</strong></span>
                      <br />
                      <span class="text-muted"
                        >Allow customers to view their previous transactions and
                        points histories.</span
                      >
                    </div>
                  </div>
                </div>
              </div>
              <!-- /4th row -->
            </div>

            <hr />

            <h3>Theme</h3>
            <br />
            <div class="col">
              <div class="row">
                <div class="col-3">
                  <base-input
                    label="Header Bar Color"
                    input-classes="form-control-sm"
                  >
                    <a-input
                      v-model="formData.header_bar_color"
                      :disabled="viewMode"
                    />
                  </base-input>

                  <base-input
                    label="Background Color"
                    input-classes="form-control-sm"
                  >
                    <a-input
                      v-model="formData.background_color"
                      :disabled="viewMode"
                    />
                  </base-input>
                </div>

                <div class="col-3">
                  <base-input
                    label="Title Font"
                    input-classes="form-control-sm"
                  >
                    <a-select
                      :disabled="viewMode"
                      v-model="formData.title_font"
                    >
                      <a-select-option
                        v-for="font in options.fonts"
                        :key="font.key"
                        :value="font.key"
                      >
                        {{ font.value }}
                      </a-select-option>
                    </a-select>
                  </base-input>

                  <base-input label="Body Font" input-classes="form-control-sm">
                    <a-select :disabled="viewMode" v-model="formData.body_font">
                      <a-select-option
                        v-for="font in options.fonts"
                        :key="font.key"
                        :value="font.key"
                      >
                        {{ font.value }}
                      </a-select-option>
                    </a-select>
                  </base-input>
                </div>
              </div>
            </div>

            <hr />

            <h3>Customization</h3>
            <br />

            <div class="col">
              <div class="row">
                <div class="col-2">
                  <base-input label="Site Logo" input-classes="form-control-sm">
                    <a-upload
                      name="site_logo"
                      list-type="picture-card"
                      class="avatar-uploader"
                      :show-upload-list="false"
                      action="/upload-file/site_logo"
                      :before-upload="beforeUpload"
                      @change="siteLogoOnChange"
                      :disabled="viewMode"
                    >
                      <img
                        v-if="formData.site_logo"
                        :src="`/${formData.site_logo}`"
                        alt="Site Logo"
                        width="100"
                        height="100"
                        :disabled="viewMode"
                      />
                      <div v-else>
                        <a-icon
                          :type="siteLogoUploading ? 'loading' : 'plus'"
                        />
                        <div class="ant-upload-text">Upload</div>
                      </div>
                    </a-upload>
                    <b-row v-if="formData.site_logo" class="col">
                      <b-col lg="4" class="pb-2"
                        ><b-button
                          size="sm"
                          variant="danger"
                          @click="removeUpload('site_logo')"
                          >Remove</b-button
                        ></b-col
                      >
                    </b-row>
                  </base-input>
                </div>

                <div class="col-2">
                  <base-input
                    label="Banner Image"
                    input-classes="form-control-sm"
                  >
                    <a-upload
                      name="banner_image"
                      list-type="picture-card"
                      class="avatar-uploader"
                      :show-upload-list="false"
                      action="/upload-file/banner_image"
                      :before-upload="beforeUpload"
                      @change="bannerImageOnChange"
                      :disabled="viewMode"
                    >
                      <img
                        v-if="formData.banner_image"
                        :src="`/${formData.banner_image}`"
                        alt="Banner Image"
                        width="100"
                        height="100"
                        :disabled="viewMode"
                      />
                      <div v-else>
                        <a-icon
                          :type="bannerImageUploading ? 'loading' : 'plus'"
                        />
                        <div class="ant-upload-text">Upload</div>
                      </div>
                    </a-upload>
                    <b-row v-if="formData.banner_image" class="col">
                      <b-col lg="4" class="pb-2"
                        ><b-button
                          size="sm"
                          variant="danger"
                          @click="removeUpload('banner_image')"
                          >Remove</b-button
                        ></b-col
                      >
                    </b-row>
                  </base-input>
                </div>

                <div class="col-2">
                  <base-input label="Site Text" input-classes="form-control-sm">
                    <a-textarea
                      v-model="formData.site_text"
                      :auto-size="{ minRows: 5, maxRows: 5 }"
                      :disabled="viewMode"
                    />
                    <div
                      v-if="$v.formData.site_text.$dirty"
                      class="text-danger"
                    >
                      <div class="error" v-if="!$v.formData.site_text.required">
                        Field is required
                      </div>
                    </div>
                  </base-input>
                </div>
              </div>
            </div>
          </div>
          <div v-if="currentStep == 2">
            <h3>Field Settings</h3>
            <br />

            <div class="py-2" style="border-bottom: 1px solid #e0e0e0">
              <a-tabs default-active-key="standard-fields">
                <a-tab-pane key="standard-fields" tab="Standard fields">
                  <div class="col-8">
                    <div class="mb-3 text-right">
                      <b-button
                        size="sm"
                        variant="warning"
                        :disabled="viewMode"
                        @click="clearFieldSelection"
                      >
                        Clear Selection
                      </b-button>
                      <b-button
                        size="sm"
                        variant="primary"
                        :disabled="viewMode"
                        @click="applyFieldDefault"
                      >
                        Apply Default
                      </b-button>
                    </div>
                    <table class="table table-outlined">
                      <thead class="thead-light">
                        <tr>
                          <th style="width: 40%">Field Name</th>
                          <th style="width: 20%" class="text-center">
                            Visible
                          </th>
                          <th style="width: 20%" class="text-center">
                            Editable
                          </th>
                          <th style="width: 20%" class="text-center">
                            Mandatory
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(item, index) in fields" :key="index">
                          <td>{{ item['form_label'] }}</td>
                          <td class="text-center">
                            <a-checkbox
                              value="item['is_visible']"
                              v-model="item['is_visible']"
                              :disabled="checkDefault(index)"
                            >
                            </a-checkbox>
                          </td>
                          <td class="text-center">
                            <a-checkbox
                              value="item['is_editable']"
                              v-model="item['is_editable']"
                              :disabled="
                                index == 'membership_number' ||
                                checkDefault(index)
                              "
                            >
                            </a-checkbox>
                          </td>
                          <td class="text-center">
                            <a-checkbox
                              value="item['is_required']"
                              v-model="item['is_required']"
                              :disabled="
                                index == 'membership_number' ||
                                checkDefault(index)
                              "
                            >
                            </a-checkbox>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </a-tab-pane>
              </a-tabs>
            </div>
          </div>

          <div v-if="currentStep == 3">
            <h3>Published Site</h3>
            <br />
            <div class="col">
              <p>{{ subDomainText }}</p>
              <qr-code :text="subDomainText"></qr-code>
            </div>
          </div>
        </div>
      </div>
      <!-- <div
        style="position: absolute; bottom: 0px;right: 0px; width: -webkit-fill-available; background-color: #f2f2f2 ;padding: 10px;"
      > -->
      <div
        style="
          position: absolute;
          bottom: 1rem;
          right: 1.5rem;
          width: -moz-webkit-fill-available;
          padding: 10px;
        "
      >
        <div class="container mx-0 px-0">
          <div class="row">
            <div class="col-auto" v-if="currentStep >= 1 && currentStep <= 2">
              <b-button
                class="float-md-left"
                style="margin-left: 8px"
                @click="previousStep"
              >
                Back
              </b-button>
            </div>

            <div class="col-auto" v-if="currentStep >= 0 && currentStep <= 1">
              <template>
                <b-button variant="success" @click="nextStep"> Next </b-button>
              </template>
            </div>
            <div class="col-auto" v-if="currentStep == 2">
              <a-spin :spinning="submittingForm">
                <template>
                  <b-button
                    variant="success"
                    :disabled="viewMode || submittingForm"
                    @click="createSitePage"
                    v-if="sitePageData === null"
                  >
                    Publish Site Page
                  </b-button>
                  <b-button
                    variant="success"
                    :disabled="viewMode || submittingForm"
                    @click="confirmUpdate"
                    v-else
                  >
                    Update Site Page
                  </b-button>
                </template>
              </a-spin>
            </div>

            <div class="col-auto" v-if="currentStep == 3">
              <template>
                <b-button
                  @click.prevent="
                    sitePageData === null
                      ? exitSitePage('created')
                      : exitSitePage('updated')
                  "
                  :disabled="viewMode"
                >
                  Close
                </b-button>
              </template>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import HTTPRequest from '../../controllers/httpRequest';
import VueQRCodeComponent from 'vue-qr-generator';
import { required, requiredIf } from 'vuelidate/lib/validators';

const BASE_URL = process.env.VUE_APP_BASE_URL;

export default {
  name: 'SitePageForm',
  props: {
    sitePageData: Object,
    viewMode: Boolean,
    drawerVisible: Boolean
  },
  components: {
    'qr-code': VueQRCodeComponent
  },
  data() {
    return {
      currentStep: 0,
      siteLogoUploading: false,
      bannerImageUploading: false,
      submittingForm: false,
      domainName: BASE_URL,
      subDomainText: '',
      fields: {},
      formData: {
        membership_program_id: null,
        site_title: null,
        site_status: null,
        sub_domain: null,
        allow_points_viewing: false,
        allow_self_registration: false,
        allow_self_update: false,
        allow_registration_without_purchase: false,
        display_banner_image: false,
        display_site_text: false,
        allow_login_with_pin: false,
        allow_trx_history_view: false,
        header_bar_color: '#24e357',
        background_color: '#24e357',
        title_font: 'Arial',
        body_font: 'Arial',
        site_logo: null,
        banner_image: null,
        site_text: null,
        field_settings: {}
      },
      options: {
        membershipPrograms: [],
        siteStatuses: [
          { key: 'A', value: 'Active' },
          { key: 'I', value: 'Inactive' },
          { key: 'X', value: 'Deactivate' }
        ],
        fonts: [
          { key: 'Arial', value: 'Arial' },
          { key: 'Tahoma', value: 'Tahoma' }
        ]
      }
    };
  },
  validations: {
    formData: {
      site_text: {
        required: requiredIf(function () {
          return this.formData.display_site_text;
        })
      },
      membership_program_id: { required },
      site_title: { required },
      site_status: { required }
    }
  },
  methods: {
    checkDefault(item) {
      switch (item) {
        case 'first_name':
        case 'last_name':
        case 'mobile_no':
          return true;
      }
      if (this.viewMode) {
        return this.viewMode;
      }
      return false;
    },
    isCheckboxDisabled(item) {
      try {
        return this.viewMode || item.has_formula;
      } catch (error) {
        return true;
      }
    },
    removeUpload(val) {
      this.formData[`${val}`] = null;
    },
    async getMembershipPrograms() {
      const response = await HTTPRequest.getRequest(
        '/api/v1/clp_membership_programs'
      );
      this.options.membershipPrograms = response.data;
    },
    async getFieldSettings() {
      const membershipProgram = this.options.membershipPrograms.find(
        (item) => item.id === this.formData.membership_program_id
      );
      const defaultFields = {
        membership_number: 'Member ID',
        first_name: 'First Name',
        middle_name: 'Middle Name',
        last_name: 'Last Name',
        last_name_suffix: 'Last Name Suffix',
        nick_name: 'Nickname',
        mobile_no: 'Mobile Number',
        email: 'Email',
        gender: 'Gender',
        birth_year: 'Birthday(Year)',
        birth_month: 'Birthday(Month)',
        birth_day: 'Birthday(Day)',
        location_unit: 'Address(Unit No.)',
        location_street: 'Address(Street)',
        location_barangay: 'Address(Barangay)',
        location_city: 'Address(City)',
        location_province: 'Address(Province)',
        location_region: 'Address(Region)',
        zipcode: 'Zip Code'
      };
      let fields = {};
      if (membershipProgram) {
        let customerFields = JSON.parse(
          membershipProgram.mandatory_customer_fields
        );
        delete customerFields.zip_code;
        Object.keys(defaultFields).forEach((key) => {
          if (!(key in customerFields)) {
            customerFields[key] = true;
          }
        });
        for (let key in customerFields) {
          if (key !== 'allow_null_mobile_no') {
            fields[key] = {
              form_label: defaultFields[key] || key,
              is_visible: this.checkDefault(key),
              is_editable: this.checkDefault(key),
              is_required: this.checkDefault(key)
            };
          }
        }
      }
      this.fields = fields;
    },
    async createSitePage() {
      this.$v.formData.$touch();
      if (!this.$v.formData.$invalid) {
        this.submittingForm = true;

        let fields = JSON.stringify(this.fields);
        this.formData.field_settings = fields;

        const response = await HTTPRequest.postRequest(
          '/api/v1/clp_site_pages',
          {
            site_page: this.formData
          }
        );

        if (response.id) {
          this.currentStep = 3;
        } else {
          if (response.error != undefined) {
            this.$notification.error({
              message: response.error
            });
          } else {
            this.$notification.error({
              message: 'Something went wrong. Please try again.'
            });
          }
        }

        this.submittingForm = false;
      }
    },
    async editSitePage() {
      this.$v.formData.$touch();

      if (!this.$v.formData.$invalid) {
        this.submittingForm = true;
        let fields = JSON.stringify(this.fields);
        this.formData.field_settings = fields;
        const response = await HTTPRequest.putRequest(
          '/api/v1/clp_site_pages/' + this.sitePageData.id,
          {
            site_page: this.formData
          }
        );

        if (response.id) {
          this.currentStep = 3;
        } else {
          if (response.error != undefined) {
            this.$notification.error({
              message: response.error
            });
          } else {
            this.$notification.error({
              message: 'Something went wrong. Please try again.'
            });
          }
        }

        this.submittingForm = false;
      }
    },

    confirmUpdate() {
      this.$confirm({
        title: 'Confirm',
        content: 'Are you sure you want to commit the changes you made?',
        onOk: () => {
          this.editSitePage();
        }
      });
    },

    exitSitePage(type) {
      this.closeDrawer();
      this.$root.$emit('refreshTable');
      if (type == 'created') {
        this.$notification.success({
          message: 'Data has been created.'
        });
      }
      if (type == 'updated') {
        this.$notification.success({
          message: 'Data has been updated.'
        });
      }
    },

    closeDrawer() {
      this.clearForm();
      this.$emit('closeDrawer', null);
      this.$v.formData.$reset();
    },
    clearForm() {
      this.formData = {
        membership_program_id: null,
        site_title: null,
        site_status: null,
        sub_domain: null,
        allow_points_viewing: false,
        allow_self_registration: false,
        allow_self_update: false,
        allow_registration_without_purchase: false,
        display_banner_image: false,
        display_site_text: false,
        allow_login_with_pin: false,
        allow_trx_history_view: false,
        header_bar_color: '#24e357',
        background_color: '#24e357',
        title_font: 'Arial',
        body_font: 'Arial',
        site_logo: null,
        banner_image: null,
        site_text: null,
        field_settings: {}
      };

      this.currentStep = 0;
      this.clearFieldSelection();
      this.$v.formData.$reset();
    },
    setFormData() {
      if (this.sitePageData !== null) {
        let {
          membership_program_id,
          site_title,
          site_status,
          sub_domain,
          allow_points_viewing,
          allow_self_registration,
          allow_self_update,
          allow_registration_without_purchase,
          display_banner_image,
          display_site_text,
          allow_login_with_pin,
          allow_trx_history_view,
          header_bar_color,
          background_color,
          title_font,
          body_font,
          site_logo,
          banner_image,
          site_text,
          field_settings
        } = this.sitePageData;

        this.fields = JSON.parse(field_settings);

        for (const item in this.fields) {
          switch (item) {
            case 'first_name':
            case 'last_name':
            case 'mobile_no':
              if (!this.fields[item].is_required) {
                this.fields[item].is_required = true;
              }
              if (!this.fields[item].is_editable) {
                this.fields[item].is_editable = true;
              }
              if (!this.fields[item].is_visible) {
                this.fields[item].is_visible = true;
              }
          }
        }

        allow_points_viewing = allow_points_viewing == 1 ? true : false;
        allow_self_registration = allow_self_registration == 1 ? true : false;
        allow_self_update = allow_self_update == 1 ? true : false;
        allow_registration_without_purchase =
          allow_registration_without_purchase == 1 ? true : false;
        display_banner_image = display_banner_image == 1 ? true : false;
        display_site_text = display_site_text == 1 ? true : false;
        allow_login_with_pin = allow_login_with_pin == 1 ? true : false;
        allow_trx_history_view = allow_trx_history_view == 1 ? true : false;
        this.subDomainText = sub_domain.replace(this.domainName, '');
        sub_domain = sub_domain.replace(this.domainName, '');

        this.formData = {
          membership_program_id,
          site_title,
          site_status,
          sub_domain,
          allow_points_viewing,
          allow_self_registration,
          allow_self_update,
          allow_registration_without_purchase,
          display_banner_image,
          display_site_text,
          allow_login_with_pin,
          allow_trx_history_view,
          header_bar_color,
          background_color,
          title_font,
          body_font,
          site_logo,
          banner_image,
          site_text,
          field_settings
        };
      }
    },

    nextStep() {
      this.$v.formData.membership_program_id.$touch();
      this.$v.formData.site_title.$touch();

      if (
        !this.$v.formData.membership_program_id.$invalid &&
        !this.$v.formData.site_title.$invalid
      ) {
        this.currentStep++;
      }
    },

    previousStep() {
      this.currentStep--;
    },
    clearFieldSelection() {
      const _fields = this.fields;

      for (const item in _fields) {
        switch (item) {
          case 'first_name':
          case 'last_name':
          case 'mobile_no':
            continue;
        }
        this.fields[item].is_visible = false;
        this.fields[item].is_editable = false;
        this.fields[item].is_required = false;
      }
    },
    applyFieldDefault() {
      const _fields = this.fields;

      for (const item in _fields) {
        if (item == 'membership_number') {
          this.fields[item].is_visible = true;
          this.fields[item].is_editable = false;
          this.fields[item].is_required = false;
        } else {
          this.fields[item].is_visible = true;
          this.fields[item].is_editable = true;
          this.fields[item].is_required = true;
        }
      }
    },

    siteLogoOnChange(res) {
      if (res.file.status === 'uploading') {
        this.siteLogoUploading = true;
        return;
      }

      if (res.file.status === 'error') {
        this.siteLogoUploading = false;
        this.$message.error('Unable to upload file. Please try again.');
      }

      if (res.file.status === 'done') {
        this.siteLogoUploading = false;
        if (
          res.file.response !== undefined ||
          res.file.response !== null ||
          res.file.response.status
        ) {
          this.formData.site_logo = res.file.response.path;
        } else {
          this.$message.error('Unable to upload file. Please try again.');
        }
      }
    },

    bannerImageOnChange(res) {
      if (res.file.status === 'uploading') {
        this.bannerImageUploading = true;
        return;
      }

      if (res.file.status === 'error') {
        this.bannerImageUploading = false;
        this.$message.error('Unable to upload file. Please try again.');
      }

      if (res.file.status === 'done') {
        this.bannerImageUploading = false;
        if (
          res.file.response !== undefined ||
          res.file.response !== null ||
          res.file.response.status
        ) {
          this.formData.banner_image = res.file.response.path;
        } else {
          this.$message.error('Unable to upload file. Please try again.');
        }
      }
    },

    beforeUpload(file) {
      const isJpgOrPng =
        file.type === 'image/jpeg' || file.type === 'image/png';
      if (!isJpgOrPng) {
        this.$message.error('You can only upload JPG file!');
      }
      const isLt2M = file.size / 1024 / 1024 < 5;
      if (!isLt2M) {
        this.$message.error('Image must smaller than 5MB!');
      }

      return isJpgOrPng && isLt2M;
    }
  },
  async created() {
    await this.getMembershipPrograms();
    await this.getFieldSettings();
    this.setFormData();
  },
  watch: {
    sitePageData: function (newVal) {
      if (newVal !== null) {
        this.setFormData();
      } else {
        this.clearForm();
      }
    },

    'formData.sub_domain': function (subDomain) {
      this.subDomainText = this.domainName + subDomain;
    },

    drawerVisible: function (val) {
      if (val == false) {
        this.closeDrawer();
      }
    }
  }
};
</script>

<style scoped></style>
